import type { Space } from '@cocoonspace/sdk-js/types/space'
import type { VenueResponse } from '@cocoonspace/sdk-js/types/venueResponse'
import type { ComponentPropsWithoutRef, ElementRef } from 'react'
import { forwardRef, useMemo } from 'react'
import {
	useGetVenue,
	useListSpaces,
} from '@cocoonspace/sdk-js/domains/spaces/spaces'
import { Card } from '@cocoonspace/ui/components/card'
import { BuildingIcon } from 'lucide-react'

import { SpaceCapacity } from '~/domains/spaces/components/atoms/space-capacity'
import { SpaceSurface } from '~/domains/spaces/components/atoms/space-surface'
import type { LocationCocoonSchema } from '../event-type-form'

const sortSpacesByIds = (spaces: Space[], ids: string[]) => {
	return spaces.sort((a, b) => {
		const aIndex = ids.indexOf(a.id)
		const bIndex = ids.indexOf(b.id)

		if (aIndex === -1 && bIndex === -1) return 0
		if (aIndex === -1) return 1
		if (bIndex === -1) return -1
		return aIndex - bIndex
	})
}

const VenueDetails = ({ venue }: { venue: VenueResponse }) => {
	const address = useMemo(() => {
		const { address1, postal_code, city } = venue?.data.address ?? {}

		return `${address1 || ''}, ${postal_code || ''} ${city || ''}`
	}, [venue])

	return (
		<div className='flex items-center gap-2'>
			<BuildingIcon />

			<div className='flex w-0 flex-1 flex-col overflow-hidden'>
				<div className='truncate text-sm'>{venue?.data.name}</div>

				<div className='text-muted-foreground truncate text-xs'>{address}</div>
			</div>
		</div>
	)
}

const CocoonRow = ({ space, index }: { space: Space; index: number }) => (
	<Card className='flex flex-row items-center gap-2 border p-2 px-4 shadow-none'>
		<div className='text-sm font-bold'>{index + 1}.</div>
		<div className='flex-1 text-sm'>
			<div>{space.title}</div>

			<div className='text-muted-foreground flex flex-row gap-4 text-xs'>
				<SpaceCapacity space={{ capacity: 4 }} />
				<SpaceSurface space={{ surface: 25 }} />
			</div>
		</div>
	</Card>
)

type LocationCocoonProps = {
	value: LocationCocoonSchema
} & ComponentPropsWithoutRef<'div'>

type LocationCocoonRef = ElementRef<'div'>

export const LocationCocoon = forwardRef<
	LocationCocoonRef,
	LocationCocoonProps
>(({ value, ...rest }, ref) => {
	const { data: venue } = useGetVenue(value.venue_id ?? '')

	const { data: spaces } = useListSpaces(
		{ ids: value.space_ids ?? [] },
		{
			query: {
				select: (res) => sortSpacesByIds(res.data, value.space_ids ?? []),
			},
		},
	)

	if (!venue) return null

	return (
		<Card
			ref={ref}
			variant='outline'
			className='flex w-full flex-col gap-2 p-4'
			{...rest}
		>
			<VenueDetails venue={venue} />

			<div className='flex flex-col gap-2'>
				{spaces?.map((item, index) => (
					<CocoonRow
						key={item.id}
						index={index}
						space={item}
					/>
				))}
			</div>
		</Card>
	)
})

LocationCocoon.displayName = 'LocationCocoon'
